import './branches.scss';
import { ButtonAttributes } from '@celebration/design-system/dist/lib/button-group/props.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClbTag } from '@celebration/react/ClbTag/ClbTag';
import { useTranslation } from 'react-i18next';
import { BreadcrumbItem } from '@celebration/react/ClbBreadcrumb/ClbBreadcrumb.types';
import Subheader from '../../common/components/subheader';
import Table from '../../common/components/table';
import ITableHead from '../../common/components/table/interfaces/ITableHead';
import UserAvatar from '../../common/components/user-avatar';
import CarriersConstants from '../carriers/constants';
import HomeConstants from '../home/constants';
import BranchesConstants from './constants';
import IBranchTableData from './interfaces/IBranchTableData';
import IPagination from '../../common/interfaces/IPagination';
import BranchesService from './services/branches.service';
import IActionButtonIcon from '../../common/components/table/interfaces/IActionButtonIcon';
import BranchFormFullPageFlow from './components/branch-form/branch-form';
import Auth from '../../app/contexts/auth';
import { SULAHUB_ROLES } from '../../common/constants/roles';
import ICarrierInfo from './interfaces/ICarrierInfo';
import BranchStatus from './enums/branch-status.enum';
import IRegion from '../../common/interfaces/IRegion';
import { getDisplayableCNPJ } from '../../utils/cnpj-utils';
import BranchDetailsConstants from '../branch-details/constants';
import IBranchInfo from '../branch-details/interfaces/IBranchInfo';
import IBranchesFiltersModel from './interfaces/IBranchesFiltersModel';
import BranchesFilters from './components/filters';
import { useToast } from '../../app/contexts/toast/toast.context';
import ProcessingError from '../../common/errors/processing-error';
import DeleteBranchModal from './components/delete-branch-modal';
import ReproveBranch from './components/reprove-branch-modal';
import ApproveBranch from './components/approve-branch-modal';

function Branches() {
    const { t } = useTranslation();
    const { isAuthorized, user } = Auth.useAuth();
    const { setToast } = useToast();

    const { state } = useLocation();
    const navigate = useNavigate();

    const [carrierInfo, setCarrierInfo] = useState<ICarrierInfo>(
        {} as ICarrierInfo,
    );

    const [branchTableData, setBranchTableData] = useState<IBranchTableData[]>(
        [],
    );
    const [fetchingData, setFetchingData] = useState(false);

    const page = useRef(0);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState<string>();

    const [isBranchFormOpened, setIsBranchFormOpened] = useState(false);

    const [editBranchId, setEditBranchId] = useState<string>();

    const [deleteDisabled, setDeleteDisabled] = useState(true);

    const [isExitModalOpen, setIsExitModalOpen] = useState(false);

    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [openReproveModal, setOpenReproveModal] = useState(false);

    const closeExitModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
    }, []);

    const confirmBranchDelete = useCallback(() => {
        setIsExitModalOpen(true);
    }, []);
    const branchesFiltersModel = useRef<IBranchesFiltersModel>({});

    const [showFilters, setShowFilters] = useState<boolean>();

    const paginatedData: IPagination<IBranchTableData> = useMemo(() => {
        return {
            data: branchTableData,
            currentPage: page.current,
            totalItems,
            totalPages,
        };
    }, [branchTableData, totalItems, totalPages]);

    const getBranchStatusTag = useCallback(
        (id: string, status: BranchStatus) => {
            switch (status) {
                case BranchStatus.approved:
                    return (
                        <ClbTag id={`${id}-tag`} type="positive">
                            {t('Aprovado')}
                        </ClbTag>
                    );
                case BranchStatus.repproved:
                    return (
                        <ClbTag id={`${id}-tag`} type="negative">
                            {t('Reprovado')}
                        </ClbTag>
                    );
                default:
                    return (
                        <ClbTag id={`${id}-tag`} type="neutral">
                            {t('AguardandoAprovacao')}
                        </ClbTag>
                    );
            }
        },
        [t],
    );

    const fetchBranchesData = useCallback(
        async (currentPage = 1) => {
            if (currentPage === 1) {
                page.current = 1;
                setTotalItems(0);
                setTotalPages(0);
                setBranchTableData([]);
            }

            setFetchingData(true);

            const branchData = await BranchesService.getBranches(
                (state as ICarrierInfo).carrierId,
                branchesFiltersModel.current,
                currentPage,
                BranchesConstants.PAGE_SIZE,
            ).catch((error: Error) => {
                setToast('negative', t('Erro'), error.message);
            });

            if (branchData) {
                const pageBranchTableData: IBranchTableData[] =
                    branchData.data.map(branch => {
                        const {
                            id,
                            corporateName,
                            cnpj,
                            sapCode,
                            tmsCode,
                            eccCode,
                            status,
                            regions,
                            requestedBy,
                            approvedAt,
                        } = branch;

                        let displayRegions = '';
                        regions.forEach((region: IRegion) => {
                            if (displayRegions.length > 0) {
                                displayRegions += '/';
                            }
                            displayRegions += region.abbreviation;
                        });

                        return {
                            id,
                            corporateName,
                            cnpj: getDisplayableCNPJ(cnpj),
                            sapCode,
                            eccCode,
                            tmsCode,
                            regions: displayRegions,
                            status: getBranchStatusTag(id, status),
                            branchStatus: status,
                            requestedBy: (
                                <UserAvatar
                                    userName={requestedBy.name}
                                    userImageUrl={requestedBy.imageUrl}
                                />
                            ),
                            approvedAt: approvedAt
                                ? new Date(approvedAt).toLocaleDateString()
                                : '',
                        };
                    });

                page.current = branchData.currentPage;
                setTotalItems(branchData.totalItems);
                setTotalPages(branchData.totalPages);
                setDeleteDisabled(true);
                setBranchTableData(current => {
                    const existingIds = new Set(current.map(item => item.id));
                    const uniqueNewItems = pageBranchTableData.filter(
                        item => !existingIds.has(item.id),
                    );

                    return current.concat(uniqueNewItems);
                });
            }

            setFetchingData(false);
        },
        [state, setToast, getBranchStatusTag, t],
    );

    useEffect(() => {
        const stateCarrierInfo = state as ICarrierInfo;

        if (!stateCarrierInfo?.carrierId) {
            navigate(CarriersConstants.PATH);
        } else if (page.current === 0) {
            setCarrierInfo({
                carrierId: state.carrierId,
                carrierName: state.carrierName,
            });
            fetchBranchesData();
        }
    }, [navigate, state, fetchBranchesData]);

    useEffect(() => {
        if (searchTerm !== undefined) {
            const delayDebounceFn = setTimeout(() => {
                branchesFiltersModel.current = {
                    ...branchesFiltersModel.current,
                    description: searchTerm,
                };
                fetchBranchesData();
            }, 300);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchTerm, fetchBranchesData]);

    const toggleShowFilters = useCallback(() => {
        setShowFilters(!showFilters);
    }, [showFilters]);

    const onApplyFilters = useCallback(
        (filtersModel: IBranchesFiltersModel) => {
            branchesFiltersModel.current = {
                ...filtersModel,
                description: searchTerm,
            };
            toggleShowFilters();
            fetchBranchesData();
        },
        [toggleShowFilters, fetchBranchesData, searchTerm],
    );

    const breadcrumbList: BreadcrumbItem[] = [
        { label: HomeConstants.PAGE_TITLE, url: HomeConstants.PATH },
        {
            label: t(
                user?.type === 'third'
                    ? t(CarriersConstants.PAGE_TITLE_SINGULAR)
                    : t(CarriersConstants.PAGE_TITLE),
            ),
            url: CarriersConstants.PATH,
        },
        { label: t(BranchesConstants.PAGE_TITLE), url: BranchesConstants.PATH },
    ];

    const toolbarIconsActionsList: IActionButtonIcon[] =
        user?.type === 'ambev'
            ? [
                  {
                      icon: 'Delete',
                      tooltipLabel: t('Deletar'),
                      callback: () => confirmBranchDelete(),
                      disabled:
                          deleteDisabled ||
                          isAuthorized(
                              SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                          ),
                  },
                  {
                      icon: 'Filter',
                      tooltipLabel: t('Filtrar'),
                      callback: () => toggleShowFilters(),
                  },
              ]
            : [];

    const toolbarButtonList: ButtonAttributes[] = useMemo(() => {
        const selectedBranches = branchTableData.filter(b => b.checked);

        if (
            selectedBranches.length === 0 &&
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ])
        ) {
            return [
                {
                    id: 'new-branch',
                    ariaLabel: t('NovaFilial'),
                    label: t('NovaFilial'),
                    title: t('NovaFilial'),
                    callback: () => setIsBranchFormOpened(true),
                },
            ];
        }

        if (
            selectedBranches.length > 0 &&
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
            ])
        ) {
            return [
                {
                    id: 'reprove-branches',
                    ariaLabel: t('ReprovarFilial'),
                    label: t('ReprovarFilial'),
                    title: t('ReprovarFilial'),
                    callback: () => setOpenReproveModal(true),
                },
                {
                    id: 'approve-branches',
                    ariaLabel: t('AprovarFilial'),
                    label: t('AprovarFilial'),
                    title: t('AprovarFilial'),
                    callback: () => setOpenApproveModal(true),
                },
            ];
        }

        return [];
    }, [branchTableData, isAuthorized, t]);

    const headers: ITableHead[] =
        user?.type === 'ambev'
            ? [
                  {
                      id: 'corporateName',
                      label: t('RazaoSocial'),
                      field: 'corporateName',
                  },
                  { id: 'cnpj', label: 'CNPJ', field: 'cnpj' },
                  {
                      id: 'sapCode',
                      label: t('CodigoSapReduzido'),
                      field: 'sapCode',
                  },
                  {
                      id: 'tmsCode',
                      label: t('CodigoTmsReduzido'),
                      field: 'tmsCode',
                  },
                  {
                      id: 'eccCode',
                      label: t('CodigoEccReduzido'),
                      field: 'eccCode',
                  },
                  {
                      id: 'regions',
                      label: t('RegioesAtendidas'),
                      field: 'regions',
                  },
                  {
                      id: 'status',
                      label: t('StatusFilial'),
                      field: 'status',
                      alignContent: 'center',
                  },
                  {
                      id: 'requestedBy',
                      label: t('SolicitadoPor'),
                      field: 'requestedBy',
                  },
                  {
                      id: 'approvedAt',
                      label: t('AprovadoEm'),
                      field: 'approvedAt',
                  },
              ]
            : [
                  {
                      id: 'corporateName',
                      label: t('RazaoSocial'),
                      field: 'corporateName',
                  },
                  { id: 'cnpj', label: 'CNPJ', field: 'cnpj' },
                  {
                      id: 'status',
                      label: t('StatusFilial'),
                      field: 'status',
                      alignContent: 'center',
                  },
              ];

    const toolbarSearchHandler = useCallback(
        async (term: string): Promise<void> => {
            setSearchTerm(term);
        },
        [setSearchTerm],
    );

    const loadMoreButtonHandler = useCallback(async (): Promise<void> => {
        await fetchBranchesData(page.current + 1);
    }, [fetchBranchesData]);

    const branchCheckboxHandler = useCallback(
        (id: string): void => {
            const branches = [...branchTableData];

            const branchToCheck = branches.find(b => b.id === id);
            branchToCheck!.checked = !branchToCheck?.checked;

            const isAnyChecked = branches.some(b => b.checked);

            setBranchTableData(branches);
            setDeleteDisabled(!isAnyChecked);
        },
        [branchTableData],
    );

    const allBranchesCheckboxHandler = useCallback(
        (allChecked: boolean): void => {
            const selectedBranches = [...branchTableData];
            selectedBranches.forEach(b => {
                b.checked = allChecked;
            });

            setBranchTableData(selectedBranches);
            setDeleteDisabled(!allChecked);
        },
        [branchTableData],
    );

    const deleteBranch = useCallback(async () => {
        setIsExitModalOpen(false);

        const branchesChecked = branchTableData.filter(b => b.checked);

        BranchesService.deleteBranches(carrierInfo.carrierId, branchesChecked)
            .then(() => {
                setToast(
                    'positive',
                    branchesChecked.length === 1
                        ? t('FilialDeletada')
                        : t('FiliaisDeletadas'),
                );
            })
            .catch(error => {
                if (error instanceof ProcessingError) {
                    setToast(
                        'warning',
                        t('FiliaisNaoDeletadas'),
                        error.message,
                    );
                } else if (error instanceof Error) {
                    setToast(
                        'negative',
                        branchesChecked.length === 1
                            ? t('FilialNaoPodeDeletar')
                            : t('FiliaisNaoPodeDeletar'),
                        error.message,
                    );
                }
            })
            .finally(() => {
                fetchBranchesData();
            });
    }, [
        branchTableData,
        fetchBranchesData,
        setToast,
        carrierInfo.carrierId,
        t,
    ]);

    const itemDetailButtonHandler = useCallback(
        ({ id, corporateName }: IBranchTableData): void => {
            const carrierInfos = state as ICarrierInfo;
            navigate(BranchDetailsConstants.PATH, {
                state: {
                    carrierId: carrierInfos.carrierId,
                    branchId: id,
                    carrierName: carrierInfos.carrierName,
                    branchName: corporateName,
                } as IBranchInfo,
            });
        },
        [navigate, state],
    );

    const itemEditButtonHandler = useCallback(({ id }: IBranchTableData) => {
        setEditBranchId(id);
        setIsBranchFormOpened(true);
    }, []);

    const itemActionsOptions: IActionButtonIcon[] = useMemo(() => {
        let actionsOptions: IActionButtonIcon[] = [];

        if (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
            ])
        ) {
            actionsOptions = [
                {
                    icon: 'Eye1',
                    tooltipLabel: t('Detalhes'),
                    callback: item => itemDetailButtonHandler(item),
                },
                {
                    icon: 'Edit',
                    tooltipLabel: t('Editar'),
                    callback: item => itemEditButtonHandler(item),
                },
            ];
        }

        if (isAuthorized([SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER])) {
            actionsOptions = [
                {
                    icon: 'Eye1',
                    tooltipLabel: t('Detalhes'),
                    callback: item => itemDetailButtonHandler(item),
                },
            ];
        }

        return actionsOptions;
    }, [isAuthorized, itemDetailButtonHandler, itemEditButtonHandler, t]);

    const pageTitle = useMemo(() => {
        let title = t(BranchesConstants.PAGE_TITLE);

        if (carrierInfo) {
            title = `${title} - ${carrierInfo.carrierName}`;
        }

        return title;
    }, [carrierInfo, t]);

    return (
        <>
            {!isBranchFormOpened && (
                <>
                    <Subheader
                        title={pageTitle}
                        breadcrumbList={
                            user?.type === 'ambev'
                                ? breadcrumbList
                                : breadcrumbList.filter(
                                      x =>
                                          x.label !==
                                              t(BranchesConstants.PAGE_TITLE) &&
                                          user?.type === 'third',
                                  )
                        }
                        dataTestid="branches-subheader"
                    />

                    <div className="branches-list" data-testid="branches-table">
                        <Table
                            content={paginatedData}
                            headers={headers}
                            fetchingData={fetchingData}
                            toolbarSearchCallback={toolbarSearchHandler}
                            toolbarIconsActions={toolbarIconsActionsList}
                            toolbarButtonList={toolbarButtonList}
                            emptyTableDescription={t('FilialNenhumaExibir')}
                            loadMoreButtonCallback={loadMoreButtonHandler}
                            itemButtonActions={itemActionsOptions}
                            selectRowCallback={
                                user?.type === 'ambev'
                                    ? branchCheckboxHandler
                                    : undefined
                            }
                            selectAllCallback={
                                user?.type === 'ambev'
                                    ? allBranchesCheckboxHandler
                                    : undefined
                            }
                        />
                    </div>
                </>
            )}
            {isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <BranchFormFullPageFlow
                    carrier={carrierInfo}
                    isBranchFormOpened={isBranchFormOpened}
                    setIsBranchFormOpened={setIsBranchFormOpened}
                    branchId={editBranchId}
                    setEditBranchId={setEditBranchId}
                    fetchBranchesData={fetchBranchesData}
                />
            )}

            {isExitModalOpen && (
                <DeleteBranchModal
                    opened={isExitModalOpen}
                    deleteConfirmedCallback={deleteBranch}
                    deleteCancelCallback={() => {
                        setIsExitModalOpen(false);
                    }}
                    closeModalCallback={closeExitModalHandler}
                    branches={branchTableData}
                />
            )}

            {openApproveModal && (
                <ApproveBranch
                    carrierId={carrierInfo.carrierId}
                    branches={branchTableData}
                    closeModalCallback={() => setOpenApproveModal(false)}
                    opened={openApproveModal}
                    fetchBranchesData={() => fetchBranchesData()}
                />
            )}

            {openReproveModal && (
                <ReproveBranch
                    carrierId={carrierInfo.carrierId}
                    branches={branchTableData}
                    closeModalCallback={() => setOpenReproveModal(false)}
                    opened={openReproveModal}
                    fetchBranchesData={() => fetchBranchesData()}
                />
            )}

            {showFilters && (
                <BranchesFilters
                    isShowingFilters={showFilters}
                    currentFilters={branchesFiltersModel.current}
                    toggleShowFilters={toggleShowFilters}
                    onApplyFilter={onApplyFilters}
                />
            )}
        </>
    );
}

export default Branches;
